import '@ant-design/v5-patch-for-react-19'
import { StrictMode } from 'react'
import { createRoot } from 'react-dom/client'
import './index.css'
import '/src/styles/_colors.scss'
import './styles/antd/index.scss'
import { Provider } from 'react-redux'
import store from './redux/store.ts'
import { RouterProvider } from 'react-router-dom'
import { remixRouter } from './router/router.tsx'
import * as echarts from 'echarts/core'
import {
  CellSpanModule,
  CellStyleModule,
  ClientSideRowModelModule,
  ColumnApiModule,
  ColumnAutoSizeModule,
  ColumnHoverModule,
  CsvExportModule,
  DateFilterModule,
  EventApiModule,
  GridStateModule,
  HighlightChangesModule,
  InfiniteRowModelModule,
  ModuleRegistry,
  NumberFilterModule,
  PaginationModule,
  PinnedRowModule,
  RowApiModule,
  RowAutoHeightModule,
  RowDragModule,
  RowSelectionModule,
  RowStyleModule,
  ScrollApiModule,
  TextFilterModule,
  TooltipModule,
} from 'ag-grid-community'
import {
  CellSelectionModule,
  ClipboardModule,
  ColumnMenuModule,
  ContextMenuModule,
  ExcelExportModule,
  FindModule,
  LicenseManager,
  MasterDetailModule,
  RowGroupingModule,
  RowNumbersModule,
  ServerSideRowModelApiModule,
  ServerSideRowModelModule,
  SetFilterModule,
} from 'ag-grid-enterprise'
import './i18n.ts'
import echartsLightTheme from './styles/echarts/echarts-theme-light.json'
import echartsDarkTheme from './styles/echarts/echarts-theme-dark.json'
import { isProd, isInternalWebsite } from './utils/helpers.ts'
import { Auth0Provider } from '@auth0/auth0-react'
import { TutorialProvider } from './contexts/tutorial-context.tsx'
import { Auth0RefreshBridge } from './common/auth/auth0-refresh-bridge.tsx'

const env = import.meta.env

ModuleRegistry.registerModules([
  MasterDetailModule,
  ColumnAutoSizeModule,
  ColumnHoverModule,
  PinnedRowModule,
  RowAutoHeightModule,
  RowStyleModule,
  PaginationModule,
  RowDragModule,
  CellSpanModule,
  CellStyleModule,
  HighlightChangesModule,
  TooltipModule,
  TextFilterModule,
  NumberFilterModule,
  DateFilterModule,
  RowSelectionModule,
  CsvExportModule,
  GridStateModule,
  ColumnApiModule,
  RowApiModule,
  ScrollApiModule,
  EventApiModule,
  InfiniteRowModelModule,
  ClientSideRowModelModule,
  RowNumbersModule,
  FindModule,
  SetFilterModule,
  CellSelectionModule,
  ColumnMenuModule,
  ContextMenuModule,
  ExcelExportModule,
  ClipboardModule,
  ServerSideRowModelModule,
  ServerSideRowModelApiModule,
  RowGroupingModule,
])

// Set the license key for ag-grid-enterprise
LicenseManager.setLicenseKey(
  'Using_this_{AG_Grid}_Enterprise_key_{AG-101240}_in_excess_of_the_licence_granted_is_not_permitted___Please_report_misuse_to_legal@ag-grid.com___For_help_with_changing_this_key_please_contact_info@ag-grid.com___{Polymer_Consulting_Limited}_is_granted_a_{Single_Application}_Developer_License_for_the_application_{Praxis}_only_for_{1}_Front-End_JavaScript_developer___All_Front-End_JavaScript_developers_working_on_{Praxis}_need_to_be_licensed___{Praxis}_has_not_been_granted_a_Deployment_License_Add-on___This_key_works_with_{AG_Grid}_Enterprise_versions_released_before_{7_June_2027}____[v3]_[01]_MTgxMjMyMjgwMDAwMA==253b71b6c4078336f6059f3688dbe3de'
)

// Setting theme color for echarts
echarts.registerTheme(echartsLightTheme.themeName, echartsLightTheme.theme)
echarts.registerTheme(echartsDarkTheme.themeName, echartsDarkTheme.theme)

const ideyaTitleText = {
  production: 'Ideya',
  development: 'Ideya Dev',
  staging: 'Ideya Staging AU',
}

function setFaviconAndTitle() {
  const favicon = document.querySelector('link[rel="icon"]') as HTMLLinkElement
  const title = document.querySelector('title')
  if (!favicon || !title) return

  switch (env.VITE_PRODUCTION) {
    case '1ppon':
      favicon.href = isProd()
        ? 'http://asset.1ppon.com/fav-icon.svg'
        : 'http://asset.1ppon.com/fav-icon.svg'
      title.textContent = isProd() ? '1ppon' : '1ppon Dev'
      break
    case 'ideya':
      favicon.href = isProd()
        ? 'https://asset.polymerideya.com/ideya-prod-favicon.png'
        : 'https://asset.polymerideya.com/ideya-dev-favicon.png'
      title.textContent = ideyaTitleText[env.VITE_ENVIRONMENT] || 'Ideya'
      break
    default:
      break
  }
}

setFaviconAndTitle()
const AUTH0_DOMAIN = env.VITE_AUTH0_DOMAIN
const AUTH0_CLIENT_ID = env.VITE_AUTH0_CLIENT_ID

const root = createRoot(document.getElementById('root')!)

const app = (
  <StrictMode>
    <Provider store={store}>
      <TutorialProvider>
        <RouterProvider router={remixRouter} />
      </TutorialProvider>
    </Provider>
  </StrictMode>
)

if (isInternalWebsite) {
  root.render(app)
} else if (env.VITE_PRODUCTION == '1ppon') {
  root.render(
    <Provider store={store}>
      <Auth0Provider
        domain={AUTH0_DOMAIN}
        clientId={AUTH0_CLIENT_ID}
        authorizeTimeoutInSeconds={5}
        authorizationParams={{
          redirect_uri: window.location.origin,
        }}
      >
        <Auth0RefreshBridge />
        <TutorialProvider>
          <RouterProvider router={remixRouter} />
          {/*<Auth0Login />*/}
        </TutorialProvider>
      </Auth0Provider>
    </Provider>
  )
  //root.render(app)
} else {
  if (env.VITE_IS_AUTH0 == 'auth0') {
    root.render(
      <Provider store={store}>
        <Auth0Provider
          domain={AUTH0_DOMAIN}
          clientId={AUTH0_CLIENT_ID}
          authorizeTimeoutInSeconds={5}
          authorizationParams={{
            redirect_uri: window.location.origin,
          }}
        >
          <Auth0RefreshBridge />
          <TutorialProvider>
            <RouterProvider router={remixRouter} />
            {/*<Auth0Login />*/}
          </TutorialProvider>
        </Auth0Provider>
      </Provider>
    )
  } else {
    // Old cloudflare auth
    root.render(app)
  }
}
